import React from 'react';
import { PropTypes } from 'prop-types';
import { GetErrorsFromResponse, PrepareError, ToString } from '../utils';
import { ErrorsBlockComponent } from '../errorsBlockComponent';

const GoogleChartsLoaderUrl = 'https://www.gstatic.com/charts/loader.js';

export function LoadGoogleChartsAPI() {
    const self = LoadGoogleChartsAPI;
    if (self.IsLoaded) {
        return Promise.resolve(null);
    }
    else if (Boolean(self.LoadError)) {
        return Promise.reject(self.LoadError);
    }
    else if (!Boolean(self._promise)) {
        self._promise = new Promise((resolve, reject) => {
            $.getScript(GoogleChartsLoaderUrl)
                .done(() => {
                    google.charts.load('current', { 'packages': ['corechart'] })
                        .then(resolve)
                        .catch(error => {
                            //Google loader does not reject promise in case if error! 
                            //But wee hope :)
                            reject(error);
                        });
                })
                .fail(function (jqXHR, textStatus, errorThrown) {
                    const url = Boolean(this) ? this.url : '';
                    if (errorThrown == 'error') {
                        errorThrown = `Failed to load Google Charts loader (${GoogleChartsLoaderUrl}). Status code: ${jqXHR.status}`;
                    }
                    reject(GetErrorsFromResponse(jqXHR, textStatus, errorThrown, url));
                });
        })
        .then(() => {
            self.IsLoaded = true;
        })
        .catch(error => {
            self.IsLoadFailed = true;
            self.LoadError = PrepareError(error);
            throw error;
        })
        .finally(() => {
            self._promise = null;
        })
    }

    return self._promise;
}
LoadGoogleChartsAPI.IsLoaded = false;
LoadGoogleChartsAPI.IsLoadFailed = false;
LoadGoogleChartsAPI.LoadError = null;
LoadGoogleChartsAPI._promise = null;

const styleSympleGradient = {width:0, height:0, position:'absolute'};

export class SimpleGradient extends React.Component {

    render() {
        const self = this;
        const {id, color1, color2, offset1, offset2} = self.props;

        return (
            <svg style={styleSympleGradient} aria-hidden focusable={false}>
                <linearGradient id={id} x2={0} y2={1}>
                    <stop offset={offset1 + '%'} stopColor={color1} />
                    <stop offset={offset2 + '%'} stopColor={color2} />
                </linearGradient>
            </svg>
        )
    }
}
SimpleGradient.propTypes = {
    color1: PropTypes.string.isRequired,
    color2: PropTypes.string.isRequired,
    offset1: PropTypes.number.isRequired,
    offset2: PropTypes.number.isRequired,
}
SimpleGradient.defaultProps = {
    color2: 'white',
    offset1: 0,
    offset2: 100,
}

export class ChartBase extends React.Component {
    constructor(props, ...others) {
        super(props, ...others);

        this.chartNode = React.createRef();
        this.chart = null;
        this.drawChart = this.drawChart.bind(this);
        this.destroyChart = this.destroyChart.bind(this);
        this.processWindowResize = this.processWindowResize.bind(this);
        this.handleWindowResize = this.handleWindowResize.bind(this);
        this._width = 0;
        this._height = 0;

        this.attachEventHandlers = this.attachEventHandlers.bind(this);
        this.handleAnimationfinish = this.handleAnimationfinish.bind(this);
        this.handleClick = this.handleClick.bind(this);
        this.handleError = this.handleError.bind(this);
        this.handleLegendPagination = this.handleLegendPagination.bind(this);
        this.handleOnMouseOver = this.handleOnMouseOver.bind(this);
        this.handleOnMouseOut = this.handleOnMouseOut.bind(this);
        this.handlePage = this.handlePage.bind(this);
        this.handleRangeChange = this.handleRangeChange.bind(this);
        this.handleReady = this.handleReady.bind(this);
        this.handleRegionClick = this.handleRegionClick.bind(this);
        this.handleRollup = this.handleRollup.bind(this);
        this.handleSelect = this.handleSelect.bind(this);
        this.handleSort = this.handleSort.bind(this);
    }

    destroyChart() {
        if (Boolean(self.chart)) {
            try {
                self.chart.clearChart();
            }
            finally {
                self.chart = null;
            }
        }
    }

    componentDidMount() {
        const self = this;
        self._isMounted = true;
        self.processWindowResize(true);
        $(window).on('resize', self.handleWindowResize);
        if (!LoadGoogleChartsAPI.IsLoaded) {
            LoadGoogleChartsAPI()
                .then(() => {
                    if (self._isMounted) {
                        self.forceUpdate();
                    }
                })
                .catch(() => {
                    if (self._isMounted) {
                        self.forceUpdate();
                    }
                });
        }
        else {
            self.drawChart();
        }
    }

    componentWillUnmount() {
        const self = this;
        self._isMounted = false;
        $(window).off('resize', self.handleWindowResize);
        self.destroyChart();
    }

    componentDidUpdate() {
        this.drawChart();
    }
    
    shouldComponentUpdate(nextProps, nextState) {
        const props = this.props;

        const result = 
            (props.updateOnResize != nextProps.updateOnResize) ||
            (props.allowTooltipOverflow != nextProps.allowTooltipOverflow);

        return result;
    }

    attachEventHandlers(chart) {
        const self = this;

        if (Boolean(chart)) {
            google.visualization.events.addListener(chart, 'animationfinish', self.handleAnimationfinish);
            google.visualization.events.addListener(chart, 'click', self.handleClick);
            google.visualization.events.addListener(chart, 'error', self.handleError);
            google.visualization.events.addListener(chart, 'legendpagination', self.handleLegendPagination);
            google.visualization.events.addListener(chart, 'onmouseover', self.handleOnMouseOver);
            google.visualization.events.addListener(chart, 'onmouseout', self.handleOnMouseOut);
            google.visualization.events.addListener(chart, 'page', self.handlePage);
            google.visualization.events.addListener(chart, 'rangechange', self.handleReady);
            google.visualization.events.addListener(chart, 'ready', self.handleReady);
            google.visualization.events.addListener(chart, 'regionClick', self.handleRegionClick);
            google.visualization.events.addListener(chart, 'rollup', self.handleRollup);
            google.visualization.events.addListener(chart, 'select', self.handleSelect);
            google.visualization.events.addListener(chart, 'sort', self.handleSort);
        }
    }

    handleAnimationfinish(data) {}

    handleClick(data) {}

    handleError(data) {}

    handleLegendPagination(data) {}

    handleOnMouseOver(data) {}

    handleOnMouseOut(data){}

    handlePage(data) {}

    handleRangeChange(data) {}

    handleReady(data) {
        const self = this;
        if (self._isMounted && Boolean(self.props.allowTooltipOverflow) && Boolean(self.chartNode.current)) {
            const $svg = $(self.chartNode.current).find('svg');
            $svg.first().css('overflow', 'visible');
        }
    }

    handleRegionClick(data) {}

    handleRollup(data) {}

    handleSelect(data) {}

    handleSort(data) {}

    processWindowResize(skeepUpdate) {
        const self = this;
        if (self._isMounted && Boolean(self.chartNode.current)) {
            const $chartNode = $(self.chartNode.current);
            const w = $chartNode.outerWidth();
            const h = $chartNode.outerHeight();
            if ((self._width != w) || (self._height != h)) {
                self._width = w;
                self._height = h;
                if (!Boolean(skeepUpdate) && Boolean(self.props.updateOnResize) && Boolean(self.chart)) {
                    if (self._updateTimer) {
                        clearTimeout(self._updateTimer);
                        self._updateTimer = 0;
                    }
                    self._updateTimer = setTimeout(
                        () => {
                            if (self._isMounted) {
                                self.forceUpdate();
                            }
                        }, 
                        200);
                }
            }
        }
    }

    handleWindowResize() {
        this.processWindowResize(false);
    }

    drawChart() {
    }

    render() {
        if (!LoadGoogleChartsAPI.IsLoaded) {
            if (LoadGoogleChartsAPI.IsLoadFailed) {
                return  (
                    <div ref={this.chartNode}>
                        <ErrorsBlockComponent className="fs-xs text-danger" errors={LoadGoogleChartsAPI.LoadError} unstyled />
                    </div>
                )
            }
            else {
                return null;
            }
        }
        else {
            return <div ref={this.chartNode} />
        }
    }
}
ChartBase.ChartFontName1 = "'Open Sans','Helvetica Neue',Helvetica,Arial,Verdana,sans-serif";
ChartBase.ChartFontName = '"Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, Arial, "Helvetica Neue", "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"';
ChartBase.ChartFontSize = 12;
ChartBase.propTypes = {
    updateOnResize: PropTypes.bool,
    allowTooltipOverflow: PropTypes.bool,
}
ChartBase.defaultProps = {
    updateOnResize: true,
    allowTooltipOverflow: true,
}
ChartBase.getTextSize = (text, fontSize, fontName) => {
    fontName = ToString(fontName) || ChartBase.ChartFontName;
    if (typeof fontSize !== 'string') {
        fontSize = (parseInt(fontSize) || ChartBase.ChartFontSize) + 'px';
    }

    const $element = $(`<span id="textsizetester-${Date.now}" style="font-family:${fontName};font-size:${fontSize};position:absolute;left:-200%;">${text}</span>`)
        .appendTo(document.body);
    const result = {w: $element.outerWidth(), h: $element.outerHeight()};
    $element.remove();
    return result;
}
